import { A, useNavigate, useParams } from "@solidjs/router";
import {
  Activity,
  ArrowLeft,
  Bot,
  Camera,
  EllipsisVertical,
  MessageSquare,
  Pencil,
  Phone,
  ScanEye,
  ScreenShare,
  Smartphone,
  Trash2,
} from "lucide-solid";
import { createMemo, createSignal, onMount, Show } from "solid-js";
import { toast } from "somoto";
import { BatteryBadge, OnlineBadge, ToneBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TextField, TextFieldInput } from "@/components/ui/text-field";
import { api, errorText } from "@/lib/api";
import { now } from "@/lib/clock";
import { ago } from "@/lib/format";
import { live } from "@/lib/live";
import { parse } from "@/lib/proto";
import { ActivityPanel } from "./device/Activity";
import { AgentPanel } from "./device/Agent";
import { CallsPanel } from "./device/Calls";
import { CameraPanel } from "./device/Camera";
import { MessagesPanel } from "./device/Messages";
import { RemotePanel } from "./device/Remote";
import { ScreenPanel } from "./device/Screen";

function RenameDialog(props: { open: boolean; onOpenChange: (o: boolean) => void; id: string; name: string }) {
  const [name, setName] = createSignal(props.name);
  const save = async (e: Event) => {
    e.preventDefault();
    try {
      await api.renameDevice(props.id, name());
      props.onOpenChange(false);
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPortal>
        <DialogContent class="sm:max-w-sm">
          <form onSubmit={save} class="grid gap-4">
            <DialogHeader>
              <DialogTitle>Rename device</DialogTitle>
            </DialogHeader>
            <TextField value={name()} onChange={setName}>
              <TextFieldInput maxLength={64} autofocus />
            </TextField>
            <DialogFooter>
              <Button type="submit" disabled={!name().trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

function RemoveDialog(props: { open: boolean; onOpenChange: (o: boolean) => void; id: string; name: string }) {
  const navigate = useNavigate();
  const remove = async () => {
    try {
      await api.removeDevice(props.id);
      toast.success(`${props.name} removed`);
      navigate("/");
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPortal>
        <DialogContent class="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove {props.name}?</DialogTitle>
            <DialogDescription>
              Its certificate is revoked and it is disconnected. To use it again, enroll it with a new code.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => props.onOpenChange(false)}>
              Keep
            </Button>
            <Button variant="destructive" onClick={remove}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

const TABS = [
  { value: "agent", label: "Agent", icon: Bot },
  { value: "remote", label: "Remote", icon: ScreenShare },
  { value: "screen", label: "Screen", icon: ScanEye },
  { value: "calls", label: "Calls", icon: Phone },
  { value: "messages", label: "Messages", icon: MessageSquare },
  { value: "camera", label: "Camera", icon: Camera },
  { value: "activity", label: "Activity", icon: Activity },
];

export function DevicePage() {
  const params = useParams<{ id: string }>();
  const device = () => live.devices[params.id];
  const caps = createMemo(() => {
    const c = device()?.capabilities;
    return c ? parse.caps(c) : undefined;
  });
  const [tab, setTab] = createSignal(new URLSearchParams(location.search).get("tab") ?? "agent");
  const [renaming, setRenaming] = createSignal(false);
  const [removing, setRemoving] = createSignal(false);
  const [loaded, setLoaded] = createSignal(false);
  onMount(() => setTimeout(() => setLoaded(true), 1500));

  const changeTab = (t: string) => {
    setTab(t);
    history.replaceState(null, "", `?tab=${t}`);
  };

  return (
    <Show
      when={device()}
      fallback={
        <Show when={loaded() || live.connected} fallback={<Card class="h-40 animate-pulse" />}>
          <Card class="items-center gap-3 p-12 text-center">
            <Smartphone class="text-muted-foreground size-8" />
            <div class="font-medium">Device not found</div>
            <A href="/" class="text-sm underline underline-offset-4">
              Back to devices
            </A>
          </Card>
        </Show>
      }
    >
      {(d) => (
        <div class="space-y-5">
          <div class="flex flex-wrap items-start gap-3">
            <Button as={A} href="/" variant="ghost" size="icon-sm" aria-label="All devices" class="mt-0.5">
              <ArrowLeft />
            </Button>
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h1 class="truncate text-2xl font-semibold tracking-tight">{d().name}</h1>
                <OnlineBadge online={d().online} />
                <Show when={d().online && d().battery}>
                  {(b) => <BatteryBadge percent={b().percent} charging={b().charging} />}
                </Show>
                <Show when={d().activeTaskId}>
                  <ToneBadge tone="busy">
                    <Bot /> Working on a task
                  </ToneBadge>
                </Show>
              </div>
              <div class="text-muted-foreground mt-0.5 text-sm">
                {d().model}
                <Show when={d().fistosVersion}> · FistOS {d().fistosVersion}</Show>
                <Show when={!d().online}> · last seen {ago(d().lastSeenAt, now())}</Show>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger as={Button<"button">} variant="outline" size="icon-sm" aria-label="Device menu">
                <EllipsisVertical />
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent>
                  <DropdownMenuItem onSelect={() => setRenaming(true)}>
                    <Pencil /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem class="text-destructive" onSelect={() => setRemoving(true)}>
                    <Trash2 /> Remove device
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
          </div>

          <Tabs value={tab()} onChange={changeTab}>
            <div class="-mx-4 overflow-x-auto px-4 pb-1">
              <TabsList>
                <TabsIndicator />
                {TABS.map((t) => (
                  <TabsTrigger value={t.value} class="px-3">
                    <t.icon />
                    <span class="hidden sm:inline">{t.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <TabsContent value="agent" class="pt-3">
              <AgentPanel device={d()} />
            </TabsContent>
            <TabsContent value="remote" class="pt-3">
              <RemotePanel device={d()} caps={caps()} />
            </TabsContent>
            <TabsContent value="screen" class="pt-3">
              <ScreenPanel device={d()} caps={caps()} />
            </TabsContent>
            <TabsContent value="calls" class="pt-3">
              <CallsPanel device={d()} caps={caps()} />
            </TabsContent>
            <TabsContent value="messages" class="pt-3">
              <MessagesPanel device={d()} caps={caps()} />
            </TabsContent>
            <TabsContent value="camera" class="pt-3">
              <CameraPanel device={d()} caps={caps()} />
            </TabsContent>
            <TabsContent value="activity" class="pt-3">
              <ActivityPanel device={d()} caps={caps()} onRemove={() => setRemoving(true)} />
            </TabsContent>
          </Tabs>

          <Show when={renaming()}>
            <RenameDialog open={renaming()} onOpenChange={setRenaming} id={d().id} name={d().name} />
          </Show>
          <RemoveDialog open={removing()} onOpenChange={setRemoving} id={d().id} name={d().name} />
        </div>
      )}
    </Show>
  );
}
